import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import { useUtmCapture } from "@/lib/utm";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Building2, GraduationCap, HandCoins, BookOpen, ArrowLeft, CheckCircle2,
  Loader2, Sparkles, Send, Award, Users, TrendingUp, MessageSquare,
  Briefcase, School,
} from "lucide-react";

/**
 * Public "Become a Partner" page — role-tabbed benefits + a custom join
 * request form per role. Submits by POSTing an auto-generated password
 * to /api/auth/register (existing endpoint, no backend changes needed).
 * On success the user is redirected to /dashboard with a toast.
 */

const ROLES = [
  {
    key: "association",
    label: "جمعية",
    icon: Building2,
    tone: "from-teal-700 to-teal-900",
    tagline: "استقبل الطلاب، كلّفهم بمهام، وقيّم أداءهم في مكان واحد.",
    benefits: [
      "توزيع طلاب مناسبين لبرامجك.",
      "إدارة مهام وتسليمات وتقييمات إلكترونية.",
      "تقارير أداء شهرية جاهزة للمشاركة.",
      "إصدار شهادات موثّقة بـ QR عند الاعتماد.",
    ],
  },
  {
    key: "mentor",
    label: "موجّه",
    icon: GraduationCap,
    tone: "from-sky-700 to-teal-800",
    tagline: "شارك خبرتك مع طلاب مهتمين عبر جلسات أسبوعية.",
    benefits: [
      "طلاب موزّعون بحسب نقاط قوتك.",
      "جدولة جلسات، ملاحظات، وتقييم أداء.",
      "لوحة عمليات مركّزة على أولويات العمل.",
      "شهادة توجيه رسمية من المنصة.",
    ],
  },
  {
    key: "donor",
    label: "مؤسسة مانحة",
    icon: HandCoins,
    tone: "from-amber-600 to-rose-700",
    tagline: "موّل برامج مؤثّرة وتابع أثرك بأرقام واضحة.",
    benefits: [
      "لوحة أداء شهرية لكل برنامج ممول.",
      "تقارير PDF قابلة للمشاركة مع الشركاء.",
      "قصص المستفيدين موثّقة ومصحوبة بالأرقام.",
      "خيار Whitelabel للتقارير باسم مؤسستكم.",
    ],
  },
  {
    key: "content_creator",
    label: "معدّ محتوى",
    icon: BookOpen,
    tone: "from-teal-800 to-slate-900",
    tagline: "ابنِ مقررات تفاعلية ووسّع أثرك التعليمي.",
    benefits: [
      "استوديو محتوى مع أقسام وفيديوهات واختبارات.",
      "تحليلات أداء الاختبارات وآراء المتعلّمين.",
      "توزيع مقرراتك على برامج شريكة متعددة.",
      "شهادات إتمام موثّقة للطلاب.",
    ],
  },
  {
    key: "recruiter",
    label: "جهة استقطاب",
    icon: Briefcase,
    tone: "from-slate-800 to-teal-900",
    tagline: "تواصل مع مواهب مؤهّلة أتمّت رحلة التدريب التعاوني.",
    benefits: [
      "وصول للمواهب المؤهلة بعد إتمام الرحلة.",
      "الاطلاع على معلومات المتدربين المسموح بها فقط.",
      "طلب التواصل مع الطلاب وفق موافقتهم الصريحة.",
      "قوائم مرشحين مصنّفة حسب التخصص والمنطقة.",
    ],
  },
  {
    key: "faculty",
    label: "عضو هيئة تدريس جامعي",
    icon: School,
    tone: "from-teal-900 to-slate-950",
    tagline: "تابع تقدّم طلابك في التدريب التعاوني أكاديميًا.",
    benefits: [
      "متابعة تقدم الطالب أكاديميًا عبر لوحة مخصّصة.",
      "ربط الساعات والمهام والتقييمات بمتطلبات التدريب التعاوني.",
      "تقارير جامعية جاهزة لختم عمادة التدريب.",
      "قناة تواصل مع الجمعية والموجّه لضمان الجودة.",
    ],
  },
];

const AUTOGEN_PASSWORD = () => `Emad@${Math.random().toString(36).slice(2, 8)}${new Date().getFullYear()}`;

export default function Partners() {
  useUtmCapture();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("association");
  const [landing, setLanding] = useState(null);

  // Support deep-link ?role=recruiter
  useEffect(() => {
    const r = searchParams.get("role");
    if (r && ROLES.some((x) => x.key === r)) setTab(r);
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/landing");
        setLanding(data);
      } catch {
        setLanding(null);
      }
    })();
  }, []);

  const active = ROLES.find((r) => r.key === tab);

  return (
    <div className="min-h-screen bg-cream-50" data-testid="partners-page">
      <PublicNav />

      {/* Hero */}
      <section className="cream-gradient border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-600">شركاء النجاح</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-heading font-black text-slate-900 leading-tight">
            كن جزءًا من منظومة عماد
          </h1>
          <p className="mt-4 text-lg text-slate-700 max-w-2xl leading-relaxed">
            اختر الدور المناسب لجهتك، تعرّف على الأدوات المتاحة لك، وسجّل طلب الانضمام في نموذج مخصّص خلال دقيقتين.
          </p>
        </div>
      </section>

      {/* Role Tabs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const activeTab = r.key === tab;
            return (
              <button
                key={r.key}
                onClick={() => setTab(r.key)}
                className={`p-4 rounded-2xl border transition-all text-start ${
                  activeTab
                    ? "bg-white border-teal-300 card-shadow-lg ring-2 ring-teal-500/20"
                    : "bg-white/50 border-cream-200 hover:border-teal-200"
                }`}
                data-testid={`partner-tab-${r.key}`}
                aria-pressed={activeTab}
              >
                <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${r.tone} text-white grid place-items-center mb-3`}>
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="font-heading font-bold text-slate-900 text-sm">{r.label}</div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{r.tagline}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Body: Benefits + Form */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid gap-8 lg:grid-cols-5">
          {/* Benefits */}
          <div className="lg:col-span-2">
            <div className={`p-5 rounded-3xl bg-gradient-to-br ${active.tone} text-white relative overflow-hidden`}>
              <div className="absolute -top-16 -end-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/80 mb-4">
                  <Sparkles className="h-3.5 w-3.5" /> ماذا يمنحك دور {active.label}؟
                </div>
                <h2 className="text-2xl font-heading font-black leading-tight">{active.tagline}</h2>
                <ul className="mt-6 space-y-3">
                  {active.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/95">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-amber-300" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-testid="partners-live-stats">
              <Micro icon={Users} value={landing?.stats?.students?.display ?? "—"} label="طالب مسجّل" />
              <Micro icon={Award} value={landing?.stats?.certificates?.display ?? "—"} label="شهادة صادرة" />
              <Micro icon={TrendingUp} value={landing?.stats?.associations?.display ?? "—"} label="جمعية شريكة" />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <PartnerForm role={active} onSuccess={() => navigate("/dashboard")} />
          </div>
        </div>
      </section>

      {/* Footer note */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="p-4 rounded-2xl bg-white border border-cream-200 flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-teal-50 text-teal-700 grid place-items-center shrink-0">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-slate-900">هل لديك استفسار مخصّص؟</div>
            <p className="text-sm text-slate-600 mt-1">
              راسلنا مباشرة عبر <Link to="/contact" className="text-teal-700 font-semibold hover:underline">صفحة التواصل</Link>
              {" "}أو راجع <Link to="/faq" className="text-teal-700 font-semibold hover:underline">الأسئلة الشائعة</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Micro({ icon: Icon, value, label }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200">
      <Icon className="h-5 w-5 text-teal-700 mx-auto" strokeWidth={1.75} />
      <div className="mt-2 font-heading font-black text-slate-900 arabic-numerals">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function PartnerForm({ role, onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    organization_name: "",
    specialization: "",
    message: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const orgRequired = ["association", "donor", "recruiter", "faculty"].includes(role.key);
  const specRequired = ["mentor", "content_creator", "faculty"].includes(role.key);

  const orgLabel = {
    donor: "اسم المؤسسة المانحة",
    recruiter: "اسم جهة الاستقطاب",
    faculty: "اسم الجامعة",
    association: "اسم الجمعية",
  }[role.key] || "اسم الجهة";

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email) {
      setError("الاسم والبريد الإلكتروني مطلوبان");
      return;
    }
    if (orgRequired && !form.organization_name) {
      setError(`${orgLabel} مطلوب`);
      return;
    }
    if (specRequired && !form.specialization) {
      setError("التخصص مطلوب");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: AUTOGEN_PASSWORD(),
        role: role.key,
        phone: form.phone || undefined,
        city: form.city || undefined,
      };
      if (orgRequired) payload.organization_name = form.organization_name;
      if (specRequired) payload.specialization = form.specialization;
      await api.post("/auth/register", payload);
      toast.success("تم استلام طلبك ✓ نتواصل معك خلال 48 ساعة");
      onSuccess?.();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className="p-4 lg:p-8 rounded-3xl bg-white border border-cream-200 card-shadow"
      data-testid="partner-form"
    >
      <h3 className="font-heading font-black text-2xl text-slate-900">
        طلب انضمام — {role.label}
      </h3>
      <p className="text-sm text-slate-600 mt-1">
        نموذج مخصّص لدور <span className="font-semibold text-teal-700">{role.label}</span>. سنراجع طلبك ونتواصل معك.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={orgRequired ? "اسم ممثل الجهة" : "الاسم الكامل"} value={form.name} onChange={(v) => update("name", v)} testid="pf-name" required />
        <Field label="البريد الإلكتروني" type="email" dir="ltr" value={form.email} onChange={(v) => update("email", v)} testid="pf-email" required />
        <Field label="رقم الجوال" dir="ltr" value={form.phone} onChange={(v) => update("phone", v)} testid="pf-phone" />
        <Field label="المدينة" value={form.city} onChange={(v) => update("city", v)} testid="pf-city" />
        {orgRequired && (
          <Field label={orgLabel} value={form.organization_name} onChange={(v) => update("organization_name", v)} testid="pf-org" wide required />
        )}
        {specRequired && (
          <Field label={role.key === "faculty" ? "القسم / الكلية" : "التخصص"} value={form.specialization} onChange={(v) => update("specialization", v)} testid="pf-spec" wide required placeholder="مثال: التسويق الرقمي، تقنية المعلومات..." />
        )}
        <Field
          label="ملاحظات إضافية (اختياري)"
          value={form.message}
          onChange={(v) => update("message", v)}
          testid="pf-message"
          wide
          textarea
        />
      </div>

      {error && (
        <div role="alert" className="mt-5 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="pf-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50 transition-colors"
        data-testid="pf-submit"
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        إرسال طلب الانضمام
      </button>

      <div className="mt-4 text-xs text-slate-500 flex items-start gap-2">
        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-teal-700 shrink-0" />
        <span>بإرسال هذا الطلب فأنت توافق على <Link to="/terms" className="text-teal-700 hover:underline">شروط الاستخدام</Link> و<Link to="/privacy" className="text-teal-700 hover:underline">سياسة الخصوصية</Link>.</span>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, testid, type = "text", dir, wide, textarea, placeholder, required }) {
  const cls = "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all";
  const fieldId = testid || undefined;
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label htmlFor={fieldId} className="block text-sm font-medium text-slate-700 mb-2">
        {label} {required && <span className="text-red-500" aria-hidden="true">*</span>}
      </label>
      {textarea ? (
        <textarea
          id={fieldId}
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={testid}
          placeholder={placeholder}
          aria-label={label}
        />
      ) : (
        <input
          id={fieldId}
          type={type}
          dir={dir}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={testid}
          placeholder={placeholder}
          required={required}
          aria-label={label}
        />
      )}
    </div>
  );
}
